import {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  Fragment,
} from "react";
import { useNavigate } from "react-router-dom";

import { api, ApiError } from "~/api";
import { Button } from "~/components/Button";
import { Checkbox } from "~/components/Checkbox";
import { DatadogLinks } from "~/components/DatadogDashboardLink";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/Dialog";
import { Icon, SpinnerIcon } from "~/components/icon";
import { Input } from "~/components/input";
import { useModal } from "~/contexts/modal";
import { useGitRefresh } from "~/hooks/useGitRefresh";
import { useKeybindings } from "~/hooks/useKeyBindings";
import {
  namespacePath,
  OperationArgumentField,
} from "~/routes/clusters.$clusterId";
import {
  isByocCluster,
  podPinned,
  podPinnedNamespace,
  type Cluster,
  type CustomerSearchResult,
  type Fleet,
  type GitDetails,
  type Namespace,
  type OrgSearchResult,
  type PodNodeInfo,
  type VersionGateBlock,
} from "~/types";
import {
  cn,
  formatGitSha,
  getDatadogTidyJobLogsUrl,
  pluralize,
  stripTagPrefix,
} from "~/utils";
import {
  generateOperationShortcuts,
  getAvailableCommands,
  getCommandDisplayName,
  isActionCommand,
  isFleetOperationKind,
  getExecutionOperation,
  executeCommandAction,
  getArguments,
  getFormDataTransformer,
  getFormNote,
  isAliasCommand,
  isOperationHidden,
} from "~/utils/commandConfig";
import { DEFAULT_FLEET, orderByFleet } from "~/utils/fleet";
import { formDataToJson } from "~/utils/formDataHelpers";
import {
  operationNotes,
  ShaDetails,
  sortedArgumentEntries,
} from "~/utils/operationUtils";
import {
  scaleReplicasDelta,
  SCALE_ROLES,
  scaleResources,
  scaleTargetRows,
} from "~/utils/scaleOperation";
import { rankMatches } from "~/utils/searchMatch";

import { FleetCreationResult } from "./FleetCreationResult";
import { type Wave, FleetWaveEditor } from "./FleetWaveEditor";
import { KubectlCopyCommand } from "./KubectlCopyCommand";
import { OrgName } from "./OrgSearchModal";
import { TagsEditor } from "./tags";

interface EntityRow {
  key: string;
  sectionTitle: string;
  left: React.ReactNode;
  right: string;
  href: string;
}

type PaletteItem =
  | { kind: "command"; key: string; sectionTitle: string; commandId: string }
  | ({ kind: "entity" } & EntityRow);

const MAX_ENTITY_RESULTS = 5;

const clusterRow = (cluster: Cluster): EntityRow => ({
  key: `cluster:${cluster.id}`,
  sectionTitle: "Clusters",
  left: <span className="font-mono font-medium">{cluster.id}</span>,
  right: cluster.customer ?? cluster.tags?.kind ?? "",
  href: `/clusters/${cluster.id}`,
});

const orgRow = (org: OrgSearchResult): EntityRow => ({
  key: `org:${org.id}`,
  sectionTitle: "Orgs",
  left: <OrgName org={org} />,
  right: org.id,
  href: `/orgs/${org.id}`,
});

const customerRow = (customer: CustomerSearchResult): EntityRow => ({
  key: `customer:${customer.id}`,
  sectionTitle: "Customers",
  left: <span className="font-medium">{customer.name}</span>,
  right: customer.id,
  href: `/customers/${customer.id}`,
});

const namespaceRow = (ns: Namespace): EntityRow => ({
  key: `namespace:${ns.org_id}/${ns.region}/${ns.id}`,
  sectionTitle: "Namespaces",
  left: <span className="font-mono font-medium">{ns.id}</span>,
  right: `${ns.org_id} · ${ns.region}`,
  href: namespacePath(ns.org_id, ns.region, ns.id),
});

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="px-3 pb-1 pt-2 text-[10px] font-medium uppercase tracking-wide text-gray-400">
      {title}
    </div>
  );
}

function VersionGateBlockError({ block }: { block: VersionGateBlock }) {
  const sha = block.gate.required_min_sha;
  return (
    <div className="space-y-2 rounded-md border border-orange-200 bg-orange-50 p-3 text-sm">
      <p>
        Upgrade these clusters to checkpoint{" "}
        <a
          href={`https://github.com/turbopuffer/turbopuffer/commit/${sha}`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-blue-700 hover:underline"
        >
          {formatGitSha(sha)}
        </a>{" "}
        before crossing{" "}
        <a
          href={block.gate.tag_url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-blue-700 hover:underline"
        >
          {block.gate.name}
        </a>
        .
      </p>
      <div className="space-y-1">
        {block.clusters.map((clusterId) => {
          const params = new URLSearchParams({
            operation: "upgrade",
            target_version: sha,
          });
          return (
            <div
              key={clusterId}
              className="flex items-center justify-between gap-3"
            >
              <span className="truncate font-mono">{clusterId}</span>
              <Button asChild size="sm" variant="outline">
                <a
                  href={`/clusters/${encodeURIComponent(clusterId)}?${params}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Upgrade to checkpoint
                </a>
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const APPROVAL_BADGE_STYLES: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  manual: { bg: "bg-orange-100", text: "text-orange-700", label: "manual" },
  external_execution: {
    bg: "bg-blue-100",
    text: "text-blue-700",
    label: "external",
  },
  auto: { bg: "bg-green-100", text: "text-green-700", label: "auto" },
};

async function withGithubBypass<T>(
  args: Record<string, any>,
  run: (args: Record<string, any>) => Promise<T>,
): Promise<T> {
  try {
    return await run(args);
  } catch (error) {
    if (
      args.operation !== "upgrade" ||
      args.force ||
      !(
        error instanceof Error && error.message === "GitHub API is unavailable"
      ) ||
      !confirm(
        "GitHub API is unavailable. Deploy anyway? Make sure you're not deploying an old SHA.",
      )
    ) {
      throw error;
    }
    args.force = true;
    return run(args);
  }
}

function ApprovalBadge({ behavior }: { behavior: string | null | undefined }) {
  if (!behavior || !APPROVAL_BADGE_STYLES[behavior]) return null;
  const { bg, text, label } = APPROVAL_BADGE_STYLES[behavior];
  return (
    <span className={`rounded-full ${bg} px-2 py-0.5 text-xs ${text}`}>
      {label}
    </span>
  );
}

function formatDelta(delta: number | null): string {
  if (delta === null) return "—";
  return delta > 0 ? `+${delta}` : String(delta);
}

/** Editable replicas table for one query deployment's targets, rendered
 * inside the shared operation form. A hidden input submits every row's
 * desired replicas as absolute values, unchanged rows included; the scale
 * validations check them before executing. */
function ScaleDeploymentTable({
  deployment,
  targets,
  initialTargets,
}: {
  deployment: string;
  targets: Record<string, number | null>;
  /** Desired replicas from a retried operation, keyed by target. */
  initialTargets?: Record<string, number>;
}) {
  // Snapshot the rows when the table opens: the cluster keeps refreshing in
  // the background, and live currents would reshuffle the table mid-edit.
  const [rows] = useState(() => scaleTargetRows(targets));
  const [desired, setDesired] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      rows.map((row) => [
        row.target,
        String(initialTargets?.[row.target] ?? row.current),
      ]),
    ),
  );

  return (
    <>
      <input type="hidden" name="role" value="Query" />
      <input
        type="hidden"
        name="query_deployment"
        value={JSON.stringify({
          name: deployment,
          // Serialize non-digit inputs as null so validation rejects them:
          // Number("") is 0, which would turn a cleared field into a
          // scale-to-zero.
          targets: Object.fromEntries(
            rows.map((row) => {
              const input = (desired[row.target] ?? "").trim();
              return [row.target, /^\d+$/.test(input) ? Number(input) : null];
            }),
          ),
        })}
      />
      <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-6 gap-y-1 text-sm">
        <span className="text-xs text-gray-500">target</span>
        <span className="text-center text-xs text-gray-500">current</span>
        <span className="text-center text-xs text-gray-500">desired</span>
        <span className="w-10 text-right text-xs text-gray-500">delta</span>

        {rows.map((row, index) => {
          const delta = scaleReplicasDelta(
            row.current,
            desired[row.target] ?? "",
          );
          return (
            <div key={row.target} className="contents">
              <span className="min-w-0 truncate font-mono text-gray-700">
                {row.target}
              </span>
              <span className="text-center font-mono text-gray-900">
                {row.current}
              </span>
              <input
                type="number"
                min={0}
                required
                autoFocus={index === 0}
                value={desired[row.target] ?? ""}
                onChange={(e) =>
                  setDesired((prev) => ({
                    ...prev,
                    [row.target]: e.target.value,
                  }))
                }
                className="w-20 rounded-md border border-gray-300 px-2 py-1 text-center text-sm focus:border-blue-500 focus:outline-none"
                aria-label={`${row.target} desired replicas`}
              />
              <span
                className={cn(
                  "w-10 text-right font-mono",
                  delta ? "text-blue-700" : "text-gray-400",
                )}
              >
                {formatDelta(delta)}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}

/** Scale fields, rendered inside the shared operation form: pick a resource
 * — a machine role or, when a single cluster is selected, one of its query
 * deployments — then set replicas. Roles submit the plain role/replicas
 * arguments; query deployments get a per-target table. */
function ScaleFields({
  clusters,
  initialArguments,
}: {
  clusters: Cluster[];
  /** Arguments of a retried scale operation, used to prefill the fields. */
  initialArguments?: Record<string, any>;
}) {
  const single = clusters.length === 1 ? clusters[0] : null;
  // Query deployments are per-cluster resources, so their entries only make
  // sense for a single-cluster selection.
  const queryDeployments = single?.nodes?._metadata?.query_deployments;
  const resources = useMemo(
    () => scaleResources(queryDeployments),
    [queryDeployments],
  );

  // Roles accept lowercase aliases on the wire, so match ids loosely.
  const initialResourceId = String(
    initialArguments?.query_deployment?.name ?? initialArguments?.role ?? "",
  ).toLowerCase();
  const [resourceId, setResourceId] = useState(
    () =>
      resources.find((entry) => entry.id.toLowerCase() === initialResourceId)
        ?.id ?? "",
  );
  const resource = resources.find((entry) => entry.id === resourceId) ?? null;

  // Matches the label styling of the generic argument fields.
  const labelClass = "w-32 shrink-0 text-sm font-medium text-foreground";

  return (
    <>
      <div className="flex items-center gap-2">
        <label htmlFor="scale-resource" className={labelClass}>
          resource
        </label>
        <select
          id="scale-resource"
          required
          className="appearance-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          value={resourceId}
          onChange={(e) => setResourceId(e.target.value)}
          autoFocus={!resource}
        >
          <option value="">Select a resource…</option>
          {resources.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.label}
            </option>
          ))}
        </select>
      </div>

      {!resource ? (
        <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-800">
          Select a resource to scale.
        </div>
      ) : resource.kind === "deployment" && single ? (
        <ScaleDeploymentTable
          key={resource.id}
          deployment={resource.id}
          targets={queryDeployments?.[resource.id]?.targets ?? {}}
          initialTargets={
            initialArguments?.query_deployment?.name === resource.id
              ? initialArguments?.query_deployment?.targets
              : undefined
          }
        />
      ) : (
        <>
          <input type="hidden" name="role" value={resource.id} />
          <div className="flex items-center gap-2">
            <label htmlFor="scale-replicas" className={labelClass}>
              replicas
            </label>
            <input
              id="scale-replicas"
              name="replicas"
              type="number"
              min={0}
              required
              defaultValue={initialArguments?.replicas ?? ""}
              placeholder="Enter replicas..."
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>
        </>
      )}
    </>
  );
}

type RestartTab = "resource" | "pods";

interface RestartResource {
  id: string;
  label: string;
  argument: "name_prefix" | "query_deployment";
  value: string;
}

// Roles restart by name_prefix, using the same names as the scale form.
function restartResources(queryDeployments: string[]): RestartResource[] {
  const roles: RestartResource[] = SCALE_ROLES.map((role) => ({
    id: `role:${role}`,
    label: role,
    argument: "name_prefix",
    value: `turbopuffer-${role.toLowerCase()}`,
  }));
  const deployments: RestartResource[] = queryDeployments.map((name) => ({
    id: `deployment:${name}`,
    label: `Query – ${name.replace(/^turbopuffer-query-?/, "") || name}`,
    argument: "query_deployment",
    value: name,
  }));
  return [...roles, ...deployments];
}

function RestartFields({
  clusters,
  allAtOnce,
  initialArguments,
}: {
  clusters: Cluster[];
  allAtOnce: boolean;
  /** Arguments of a retried restart, used to prefill the fields. */
  initialArguments?: Record<string, any>;
}) {
  const single = clusters.length === 1 ? clusters[0] : null;
  // Only query deployments present on every cluster can be restarted together.
  const queryDeployments = Object.keys(
    clusters[0]?.nodes?._metadata?.query_deployments ?? {},
  )
    .filter((name) =>
      clusters.every(
        (cluster) =>
          name in (cluster.nodes?._metadata?.query_deployments ?? {}),
      ),
    )
    .sort();
  // Force restart only offers Pods, and pods are only listed for one cluster.
  const pods = single ? clusterPods(single) : [];
  const resources = allAtOnce ? [] : restartResources(queryDeployments);
  const [tab, setTab] = useState<RestartTab>(() =>
    initialArguments?.pods || resources.length === 0 ? "pods" : "resource",
  );
  const labelClass = "min-w-32 shrink-0 text-sm font-medium text-foreground";
  const tabClass = (selected: boolean) =>
    cn(
      "border-b-2 px-3 py-1.5 text-sm",
      selected
        ? "border-orange-500 font-medium"
        : "border-transparent text-muted-foreground",
    );
  const tabs: [RestartTab, string][] = [
    ...(resources.length > 0
      ? [["resource", "Resource"] as [RestartTab, string]]
      : []),
    ...(single ? [["pods", "Pods"] as [RestartTab, string]] : []),
  ];

  return (
    <>
      {tabs.length > 1 ? (
        <div
          role="tablist"
          aria-label="Restart by"
          className="flex gap-1 border-b border-gray-200"
        >
          {tabs.map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={tabClass(tab === id)}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {tab === "pods" ? (
        <RestartPodsField
          pods={pods}
          allAtOnce={allAtOnce}
          initialPods={initialArguments?.pods}
        />
      ) : (
        <RestartResourceField
          resources={resources}
          initialArguments={initialArguments}
          labelClass={labelClass}
        />
      )}
    </>
  );
}

function RestartResourceField({
  resources,
  initialArguments,
  labelClass,
}: {
  resources: RestartResource[];
  initialArguments?: Record<string, any>;
  labelClass: string;
}) {
  const [resourceId, setResourceId] = useState(
    () =>
      resources.find(
        (entry) => initialArguments?.[entry.argument] === entry.value,
      )?.id ?? "",
  );
  const resource = resources.find((entry) => entry.id === resourceId);

  return (
    <div className="flex items-center gap-2">
      <label htmlFor="restart-resource" className={labelClass}>
        resource
      </label>
      <select
        id="restart-resource"
        required
        autoFocus
        value={resourceId}
        onChange={(event) => setResourceId(event.target.value)}
        className="appearance-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
      >
        <option value="">Select a resource…</option>
        {resources.map((entry) => (
          <option key={entry.id} value={entry.id}>
            {entry.label}
          </option>
        ))}
      </select>
      {resource ? (
        <input type="hidden" name={resource.argument} value={resource.value} />
      ) : null}
    </div>
  );
}

function clusterPods(cluster: Cluster): (PodNodeInfo & { role: string })[] {
  return Object.entries(cluster.nodes ?? {})
    .filter(([role]) => role !== "_metadata")
    .flatMap(([role, pods]) =>
      ((pods as PodNodeInfo[]) ?? []).map((pod) => ({ ...pod, role })),
    )
    .sort(
      (a, b) =>
        a.role.localeCompare(b.role) || a.pod_name.localeCompare(b.pod_name),
    );
}

function PinnedNamespace({ pod }: { pod: PodNodeInfo }) {
  const bound = podPinnedNamespace(pod);
  const orgRef = useRef<HTMLSpanElement>(null);
  const namespaceRef = useRef<HTMLSpanElement>(null);
  const [showFull, setShowFull] = useState(false);
  if (!bound) {
    return (
      <span className="ml-auto text-xs italic text-gray-400">unbound</span>
    );
  }
  const truncated = () =>
    [orgRef.current, namespaceRef.current].some(
      (el) => el !== null && el.scrollWidth > el.clientWidth,
    );
  return (
    <span
      className="relative ml-auto flex min-w-0 items-center font-mono text-xs text-gray-500"
      onMouseEnter={() => setShowFull(truncated())}
      onMouseLeave={() => setShowFull(false)}
    >
      <span ref={orgRef} className="max-w-24 truncate">
        {bound.orgId}
      </span>
      <span className="shrink-0">/</span>
      <span ref={namespaceRef} className="max-w-36 truncate">
        {bound.namespace}
      </span>
      {showFull ? (
        <span
          role="tooltip"
          className="pointer-events-none absolute right-0 top-1/2 z-10 w-max max-w-sm -translate-y-1/2 break-all rounded bg-gray-800 px-2 py-1 text-white shadow-lg"
        >
          {bound.orgId}/{bound.namespace}
        </span>
      ) : null}
    </span>
  );
}

function RestartConfirmation({
  args,
  clusters,
  onBack,
  onConfirm,
}: {
  args: Record<string, any>;
  clusters: Cluster[];
  onBack: () => void;
  onConfirm: () => void;
}) {
  // Older agents without all_at_once always delete pods.
  const allAtOnce = args.all_at_once !== false;
  const pods: string[] = Array.isArray(args.pods) ? args.pods : [];
  const single = clusters.length === 1 ? clusters[0] : null;
  const podInfo = new Map(
    (single ? clusterPods(single) : []).map((pod) => [pod.pod_name, pod]),
  );
  const pinnedSelected = pods.some((name) => {
    const pod = podInfo.get(name);
    return pod !== undefined && podPinned(pod);
  });
  const verb = allAtOnce ? "force-restarted" : "rolling-restarted";
  const where =
    clusters.length > 1 ? ` on ${pluralize(clusters.length, "cluster")}` : "";

  return (
    <div className="space-y-4">
      <p className="text-sm">
        {pods.length > 0
          ? `${pluralize(pods.length, "pod")} will be ${verb}${where}:`
          : args.query_deployment
            ? `Query deployment ${args.query_deployment} will be ${verb}${where}.`
            : allAtOnce
              ? `Every pod whose name starts with ${args.name_prefix} will be ${verb}${where}.`
              : `Every StatefulSet and Deployment whose name starts with ${args.name_prefix} will be ${verb}${where}.`}
      </p>
      {pods.length > 0 ? (
        <ul className="max-h-64 overflow-auto rounded-md border border-gray-200">
          {pods.map((name) => {
            const pod = podInfo.get(name);
            return (
              <li
                key={name}
                className="flex items-center gap-2 px-3 py-1.5 text-sm"
              >
                <span className="font-mono">{name}</span>
                {pod && podPinned(pod) ? <PinnedNamespace pod={pod} /> : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {pods.length > 0 && !allAtOnce ? (
        <p className="text-sm text-muted-foreground">
          Each pod shuts down gracefully, and its disruption budget limits how
          many restart at once.
          {pinnedSelected
            ? " Pinned pods keep serving until their replacement has a warm cache."
            : null}
        </p>
      ) : null}
      {allAtOnce ? (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">
          This is a forced restart and can cause downtime.
        </div>
      ) : null}
      <p className="text-sm">Do you want to proceed?</p>
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          variant={allAtOnce ? "destructive" : "default"}
          autoFocus
          onClick={onConfirm}
        >
          Confirm
        </Button>
      </div>
    </div>
  );
}

function RestartPodsField({
  pods,
  allAtOnce,
  initialPods,
}: {
  pods: (PodNodeInfo & { role: string })[];
  allAtOnce: boolean;
  initialPods?: string[];
}) {
  const [selected, setSelected] = useState<string[]>(() =>
    pods
      .filter((pod) => initialPods?.includes(pod.pod_name))
      .map((pod) => pod.pod_name),
  );
  const [search, setSearch] = useState("");
  const selectionRef = useRef<HTMLInputElement>(null);
  const query = search.trim().toLowerCase();
  const shown = pods.filter((pod) => {
    const bound = podPinnedNamespace(pod);
    return [pod.pod_name, bound?.orgId, bound?.namespace].some((text) =>
      text?.toLowerCase().includes(query),
    );
  });

  useEffect(() => {
    const input = selectionRef.current;
    if (!input) return;
    input.value = selected.join(",");
    input.setCustomValidity(
      selected.length > 0 ? "" : "Select at least one pod",
    );
  }, [selected]);

  const toggle = (name: string, checked: boolean) =>
    setSelected((current) =>
      checked ? [...current, name] : current.filter((pod) => pod !== name),
    );

  return (
    <div className="space-y-2">
      {allAtOnce ? null : (
        <p className="text-sm text-muted-foreground">
          Restarted gracefully, as their disruption budgets allow.
        </p>
      )}
      <Input
        aria-label="Search pods"
        placeholder="Search pods..."
        value={search}
        autoFocus
        onChange={(event) => setSearch(event.target.value)}
        onKeyDown={(event) => {
          // Keep Enter in the search box from submitting the form.
          if (event.key === "Enter") event.preventDefault();
        }}
      />
      {/* Carries the selection as a comma-separated pods argument. */}
      <input
        ref={selectionRef}
        name="pods"
        tabIndex={-1}
        aria-hidden
        className="sr-only"
      />
      <div className="max-h-64 overflow-auto rounded-md border border-gray-200">
        {shown.map((pod) => (
          <label
            key={pod.pod_name}
            className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            <Checkbox
              checked={selected.includes(pod.pod_name)}
              onChange={(checked) => toggle(pod.pod_name, checked)}
              ariaLabel={pod.pod_name}
            />
            <span className="font-mono">{pod.pod_name}</span>
            {podPinned(pod) ? <PinnedNamespace pod={pod} /> : null}
          </label>
        ))}
        {shown.length === 0 ? (
          <p className="px-3 py-1.5 text-sm text-muted-foreground">
            No pods match
          </p>
        ) : null}
      </div>
    </div>
  );
}

function cloneWaves(waves: Wave[]): Wave[] {
  return waves.map((wave) => ({ ...wave, clusters: [...wave.clusters] }));
}

interface ClusterOperationsCommandPaletteProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  clusters: Cluster[];
  initialOperation?: string;
  initialArguments?: Record<string, any>;
  fleetOperation?: {
    payload: { fleet: string } | { targets: string[][] };
    waves: Wave[];
    description: string;
  };
  /**
   * Called after all selected clusters' operations succeed, so callers can
   * refetch and show the result (e.g. an edited tag) instantly instead of
   * waiting on their next poll.
   */
  onOperationComplete?: () => void;
}

export function ClusterOperationsCommandPalette({
  isOpen,
  setIsOpen,
  clusters,
  initialOperation,
  initialArguments,
  fleetOperation,
  onOperationComplete,
}: ClusterOperationsCommandPaletteProps) {
  const { openModal } = useModal();
  const [selectedOperationIndex, setSelectedOperationIndex] = useState<
    number | null
  >(0);
  const [selectedOperation, setSelectedOperation] = useState<string | null>(
    initialOperation || null,
  );
  // Where the dialog is in an execution: filling the form, a run/create in
  // flight, or settled on the results view. One value instead of separate
  // isExecuting/showResults flags that every path had to keep in sync.
  const [phase, setPhase] = useState<"form" | "running" | "done">("form");
  // Set while the restart awaits confirmation.
  const [pendingRestart, setPendingRestart] = useState<Record<
    string,
    any
  > | null>(null);
  // Client-side validation failure for the current form, shown by the button.
  const [formError, setFormError] = useState<string | null>(null);
  const [versionGateBlock, setVersionGateBlock] =
    useState<VersionGateBlock | null>(null);
  const [fleetDescription, setFleetDescription] = useState(
    fleetOperation?.description ?? "",
  );
  const [gatesEnabled, setGatesEnabled] = useState<boolean | null>(null);
  const [gateBakeMinutes, setGateBakeMinutes] = useState("");
  const [manualFleetOperation, setManualFleetOperation] = useState(false);
  const [fleetWaves, setFleetWaves] = useState<Wave[]>(() =>
    cloneWaves(fleetOperation?.waves ?? []),
  );
  const [wavesDirty, setWavesDirty] = useState(false);
  const [fleets, setFleets] = useState<Fleet[]>([]);
  // null orders by nothing: the selection as one wave.
  const [fleetName, setFleetName] = useState<string | null>(null);
  // Cached so re-picking a fleet doesn't re-resolve.
  const resolvedWaves = useRef(new Map<string, Wave[]>());
  const fleetRequest = useRef(0);
  const [resolving, setResolving] = useState(false);
  const [createdFleetOperation, setCreatedFleetOperation] = useState<
    string | null
  >(null);
  const [executionResults, setExecutionResults] = useState<
    {
      clusterId: string;
      success: boolean;
      error?: string;
      operationId?: string;
      versionGateBlock?: VersionGateBlock;
    }[]
  >([]);
  // Not always the selected command: recall_replay submits as tidy.
  const [executedOperation, setExecutedOperation] = useState<string | null>(
    null,
  );
  const [currentExecutionIndex, setCurrentExecutionIndex] = useState(0);
  const [refreshedGitDetails, setRefreshedGitDetails] =
    useState<GitDetails | null>(null);
  const { isRefreshing: isRefreshingGit, refresh: refreshGit } =
    useGitRefresh();
  const formRef = useRef<HTMLFormElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [entityRows, setEntityRows] = useState<EntityRow[]>([]);
  const [entitySearchLoading, setEntitySearchLoading] = useState(false);
  const allClustersRef = useRef<Promise<Cluster[]> | null>(null);

  // A stale checkbox must not leak into a command the fleet API rejects, so
  // eligibility gates the mode itself, not just the checkbox.
  const fleetEligible =
    selectedOperation != null && isFleetOperationKind(selectedOperation);
  const isFleetMode = Boolean(
    fleetOperation || (manualFleetOperation && fleetEligible),
  );
  const fleetClusterCount = fleetWaves.reduce(
    (count, wave) => count + wave.clusters.length,
    0,
  );

  const resetFleetState = useCallback((waves: Wave[]) => {
    setFleetWaves(cloneWaves(waves));
    setGatesEnabled(null);
    setGateBakeMinutes("");
    setWavesDirty(false);
    setFormError(null);
    setVersionGateBlock(null);
  }, []);

  const selectedIds = () => clusters.map((cluster) => cluster.id);

  // Only the picker below uses this list.
  useEffect(() => {
    if (!isFleetMode || fleetOperation || fleets.length > 0) return;
    api
      .getFleets()
      .then(setFleets)
      .catch(() => setFleets([]));
  }, [isFleetMode, fleetOperation, fleets.length]);

  const toggleFleetMode = (enabled: boolean) => {
    fleetRequest.current += 1;
    setManualFleetOperation(enabled);
    setFleetName(null);
    setFleetDescription("");
    // Show the selection right away; the default fleet's order replaces it
    // once it resolves.
    resetFleetState(orderByFleet([], selectedIds()));
    if (enabled) selectFleet(DEFAULT_FLEET);
  };

  const selectFleet = async (name: string | null) => {
    // Commit name and waves together, so a failed or superseded resolve
    // leaves the picker on what is actually in the editor.
    const request = ++fleetRequest.current;
    if (!name) {
      setFleetName(null);
      resetFleetState(orderByFleet([], selectedIds()));
      return;
    }
    let waves = resolvedWaves.current.get(name);
    if (!waves) {
      setResolving(true);
      try {
        waves = (await api.resolveFleet(name)).waves;
        resolvedWaves.current.set(name, waves);
      } catch (error) {
        if (request === fleetRequest.current) {
          setFormError(
            error instanceof Error ? error.message : "Failed to resolve fleet",
          );
        }
        return;
      } finally {
        setResolving(false);
      }
    }
    if (request !== fleetRequest.current) return;
    setFleetName(name);
    resetFleetState(orderByFleet(waves, selectedIds()));
  };

  const handleRefreshGit = useCallback(async () => {
    const freshClusters = await refreshGit();
    const matchingCluster = freshClusters.find((c) => c.id === clusters[0]?.id);
    if (matchingCluster?.git_details) {
      setRefreshedGitDetails(matchingCluster.git_details);
    }
  }, [refreshGit, clusters]);

  type ClusterBadgeState = "pending" | "executing" | "completed" | "error";

  const getClusterBadgeState = (clusterIndex: number): ClusterBadgeState => {
    // Check if this cluster had an error
    const hasError = executionResults.some(
      (result, idx) => idx === clusterIndex && !result.success,
    );
    if (hasError) return "error";

    // Check if execution is complete for this cluster
    if (clusterIndex < executionResults.length) {
      return "completed";
    }

    // Check if currently executing
    if (phase === "running" && clusterIndex === currentExecutionIndex) {
      return "executing";
    }

    // Otherwise it's pending
    return "pending";
  };

  const getBadgeStyles = (state: ClusterBadgeState) => {
    const styles = {
      pending: "bg-gray-100 text-gray-600",
      executing: "bg-blue-100 font-medium text-blue-800",
      completed: "bg-green-100 text-green-800",
      error: "bg-red-100 text-red-800",
    };
    return styles[state];
  };

  const getBadgeIcon = (state: ClusterBadgeState) => {
    switch (state) {
      case "completed":
        return " ✓";
      case "executing":
        return " →";
      case "error":
        return " ✗";
      default:
        return "";
    }
  };

  // Get the first cluster's operations for the form
  const possibleOperations = useMemo(
    () => (clusters.length > 0 ? clusters[0].possible_operations : {}),
    [clusters],
  );

  // Get all available commands (operations + configured commands/aliases/actions)
  // Already sorted by preferred order
  const { commands: operations } = useMemo(
    () => getAvailableCommands(clusters, possibleOperations),
    [clusters, possibleOperations],
  );

  const operationShortcuts = useMemo(() => {
    const ops = Object.keys(possibleOperations).filter(
      (op) => !isOperationHidden(op, clusters),
    );
    const letterToOp = generateOperationShortcuts(ops, ["J", "K"]);
    const opToLetter: Record<string, string> = {};
    for (const [letter, op] of Object.entries(letterToOp)) {
      opToLetter[op] = letter;
    }
    return opToLetter;
  }, [possibleOperations, clusters]);

  // Filter + rank operations by the search query. Matches per word by prefix,
  // so multiple space-separated tokens each need only prefix some word of the
  // command (e.g. "searc o" finds "Search Orgs"). Both the display name and
  // the raw command id are searchable. Higher-scoring (closer) matches sort
  // first; ties keep the original preferred order.
  const filteredOperations = useMemo(() => {
    if (!searchQuery) return operations;
    return rankMatches(
      operations,
      searchQuery,
      (commandId) => `${getCommandDisplayName(commandId)} ${commandId}`,
    );
  }, [operations, searchQuery]);

  // a failed fetch clears the cache
  const fetchAllClusters = useCallback(
    () =>
      (allClustersRef.current ??= api.getClusters().catch((error) => {
        allClustersRef.current = null;
        console.error("Failed to fetch clusters:", error);
        return [] as Cluster[];
      })),
    [],
  );

  const omnibarEnabled = isOpen && clusters.length === 0;

  useEffect(() => {
    if (omnibarEnabled) {
      fetchAllClusters();
    } else {
      allClustersRef.current = null;
    }
  }, [omnibarEnabled, fetchAllClusters]);

  useEffect(() => {
    const query = stripTagPrefix(searchQuery);
    if (!omnibarEnabled || selectedOperation || query.length < 2) {
      setEntityRows((prev) => (prev.length === 0 ? prev : []));
      setEntitySearchLoading(false);
      return;
    }

    let superseded = false;
    const debounceTimer = setTimeout(async () => {
      setEntitySearchLoading(true);
      const [allClusters, orgs, customers, namespaces] = await Promise.all([
        fetchAllClusters(),
        api.searchOrgs(query).catch(() => [] as OrgSearchResult[]),
        api.searchCustomers(query).catch(() => [] as CustomerSearchResult[]),
        api.searchNamespacesByPrefix(query).catch(() => [] as Namespace[]),
      ]);
      if (superseded) return;
      const matchedClusters = rankMatches(allClusters, query, (cluster) =>
        [
          cluster.id,
          cluster.customer ?? "",
          ...Object.values(cluster.tags ?? {}),
        ].join(" "),
      );
      setEntityRows([
        ...matchedClusters.slice(0, MAX_ENTITY_RESULTS).map(clusterRow),
        ...customers.slice(0, MAX_ENTITY_RESULTS).map(customerRow),
        ...orgs.slice(0, MAX_ENTITY_RESULTS).map(orgRow),
        ...namespaces.slice(0, MAX_ENTITY_RESULTS).map(namespaceRow),
      ]);
      setEntitySearchLoading(false);
    }, 150);

    return () => {
      superseded = true;
      clearTimeout(debounceTimer);
    };
  }, [omnibarEnabled, selectedOperation, searchQuery, fetchAllClusters]);

  const paletteItems = useMemo<PaletteItem[]>(
    () => [
      ...filteredOperations.map((commandId) => ({
        kind: "command" as const,
        key: `command:${commandId}`,
        sectionTitle: "Commands",
        commandId,
      })),
      ...entityRows.map((row) => ({ kind: "entity" as const, ...row })),
    ],
    [filteredOperations, entityRows],
  );

  const activeIndex =
    paletteItems.length === 0
      ? null
      : Math.min(selectedOperationIndex ?? 0, paletteItems.length - 1);

  const emptyMessage = entitySearchLoading
    ? "Searching..."
    : operations.length === 0
      ? "No common operations available for all selected clusters"
      : "No matches";

  const goDownOperationList = () => {
    if (activeIndex === null) return;
    setSelectedOperationIndex(
      activeIndex >= paletteItems.length - 1 ? 0 : activeIndex + 1,
    );
  };

  const goUpOperationList = () => {
    if (activeIndex === null) return;
    setSelectedOperationIndex(
      activeIndex <= 0 ? paletteItems.length - 1 : activeIndex - 1,
    );
  };

  // Set selectedOperation when initialOperation changes
  useEffect(() => {
    if (initialOperation && isOpen) {
      setSelectedOperation(initialOperation);
    }
  }, [initialOperation, isOpen]);

  const handleDialogClose = useCallback(() => {
    setSelectedOperationIndex(null);
    setSelectedOperation(null);
    setPhase("form");
    setPendingRestart(null);
    setFormError(null);
    setVersionGateBlock(null);
    setManualFleetOperation(false);
    setFleetDescription(fleetOperation?.description ?? "");
    resetFleetState(fleetOperation?.waves ?? []);
    setCreatedFleetOperation(null);
    setExecutionResults([]);
    setExecutedOperation(null);
    setRefreshedGitDetails(null);
    setSearchQuery("");
    setIsOpen(false);
  }, [setIsOpen, fleetOperation, resetFleetState]);

  useEffect(() => {
    if (isOpen) {
      setSelectedOperationIndex(0);
    }
  }, [isOpen, searchQuery]);

  // Scroll selected item into view
  useEffect(() => {
    if (activeIndex === null) return;
    listRef.current
      ?.querySelector(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  // Auto-focus search input when dialog opens
  useEffect(() => {
    if (isOpen && !selectedOperation) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [isOpen, selectedOperation]);

  // Handler for executing commands (operations, aliases, or actions)
  const handleCommandExecution = useCallback(
    (commandId: string) => {
      if (isActionCommand(commandId)) {
        // Execute special action
        executeCommandAction(
          commandId,
          clusters,
          handleDialogClose,
          openModal,
          navigate,
        );
      } else {
        // Show form for operation or alias
        setFormError(null);
        setVersionGateBlock(null);
        setSelectedOperation(commandId);
      }
    },
    [clusters, handleDialogClose, navigate, openModal],
  );

  const selectPaletteIndex = useCallback(
    (index: number) => {
      const item = paletteItems[index];
      if (!item) return;
      if (item.kind === "command") {
        handleCommandExecution(item.commandId);
      } else {
        handleDialogClose();
        navigate(item.href);
      }
    },
    [paletteItems, handleCommandExecution, handleDialogClose, navigate],
  );

  // Render the multi-cluster operation form
  const renderMultiClusterForm = () => {
    if (!selectedOperation) return null;

    // Check if this is a declarative alias with synthetic arguments
    const argumentsToRender = getArguments(
      selectedOperation,
      possibleOperations,
    );
    const formDataTransformer = getFormDataTransformer(selectedOperation);

    if (!argumentsToRender) return null;

    const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const form = e.currentTarget;

      // Validate form inputs before executing across multiple clusters
      if (selectedOperation && operationNotes.has(selectedOperation)) {
        const validations = operationNotes.get(selectedOperation)!.validations;
        if (validations) {
          const formData = new FormData(form);
          for (const [field, validate] of Object.entries(validations)) {
            const value = formData.get(field) as string;
            const error = validate(value);
            if (error) {
              setFormError(error);
              return;
            }
          }
        }
      }
      setFormError(null);
      setVersionGateBlock(null);

      const formData = new FormData(form);
      let jsonData: Record<string, any>;

      // If this is a declarative alias, transform the form data
      jsonData = formDataToJson(formData, argumentsToRender);

      if (formDataTransformer) {
        jsonData = formDataTransformer(jsonData);
      }

      if (getExecutionOperation(selectedOperation!) === "restart") {
        setPendingRestart(jsonData);
        return;
      }

      executeMultiClusterOperation(jsonData);
    };

    return (
      <form ref={formRef} className="space-y-4" onSubmit={handleFormSubmit}>
        <input type="hidden" name="operation" value={selectedOperation || ""} />
        <div className="space-y-4">
          {getFormNote(selectedOperation, clusters) ? (
            <div className="rounded-md bg-blue-50 p-3">
              <p className="text-sm text-blue-800">
                {getFormNote(selectedOperation, clusters)}
              </p>
            </div>
          ) : null}

          {selectedOperation === "upgrade" && clusters[0]?.git_details ? (
            <ShaDetails
              gitDetails={refreshedGitDetails ?? clusters[0].git_details}
              className="space-y-2"
              isByoc={isByocCluster(clusters[0])}
              onRefresh={handleRefreshGit}
              isRefreshing={isRefreshingGit}
            />
          ) : null}

          {selectedOperation === "edit_tags" ? (
            <TagsEditor
              initialTags={
                clusters.length === 1 ? clusters[0].custom_tags : null
              }
              derivedKeys={[
                ...new Set(
                  clusters.flatMap((c) => Object.keys(c.derived_tags ?? {})),
                ),
              ]}
            />
          ) : getExecutionOperation(selectedOperation) === "scale" ? (
            <ScaleFields
              clusters={clusters}
              initialArguments={
                selectedOperation === initialOperation
                  ? initialArguments
                  : undefined
              }
            />
          ) : isAliasCommand(selectedOperation) &&
            getExecutionOperation(selectedOperation) === "restart" ? (
            <RestartFields
              clusters={clusters}
              allAtOnce={selectedOperation === "force_restart"}
              initialArguments={
                selectedOperation === initialOperation
                  ? initialArguments
                  : undefined
              }
            />
          ) : (
            sortedArgumentEntries(argumentsToRender).map(
              ([name, properties], index) => (
                <OperationArgumentField
                  key={name}
                  name={name}
                  properties={properties}
                  index={index}
                  initialValue={
                    selectedOperation === initialOperation
                      ? initialArguments?.[name]
                      : undefined
                  }
                />
              ),
            )
          )}
        </div>

        {versionGateBlock ? (
          <VersionGateBlockError block={versionGateBlock} />
        ) : formError ? (
          <p className="text-sm text-red-500">{formError}</p>
        ) : null}
        <Button
          type="submit"
          disabled={
            phase === "running" || (isFleetMode && fleetClusterCount === 0)
          }
        >
          {phase === "running" ? (
            <SpinnerIcon className="animate-spin" />
          ) : isFleetMode ? (
            "Create Fleet Operation"
          ) : (
            `Execute on ${pluralize(clusters.length, "Cluster")}`
          )}
        </Button>
      </form>
    );
  };

  const executeMultiClusterOperation = useCallback(
    async (jsonData: Record<string, any>) => {
      setPhase("running");
      setExecutionResults([]);
      setExecutedOperation(jsonData.operation ?? null);
      setCurrentExecutionIndex(0);

      if (isFleetMode) {
        try {
          const bakeMinutes =
            gateBakeMinutes.trim() === "" ? null : Number(gateBakeMinutes);
          if (
            bakeMinutes != null &&
            (!Number.isInteger(bakeMinutes) || bakeMinutes < 0)
          ) {
            throw new Error(
              "Gate bake period must be a non-negative whole number",
            );
          }
          const description = fleetDescription.trim();
          // Only a caller-supplied run goes by name; a narrowed selection has
          // to travel as an explicit snapshot.
          const snapshot = { targets: fleetWaves.map((wave) => wave.clusters) };
          const payload = wavesDirty
            ? snapshot
            : (fleetOperation?.payload ?? snapshot);
          const created = await withGithubBypass(jsonData, async (data) => {
            const { operation, ...operationArguments } = data;
            return api.createFleetOperation({
              // force upgrade retries skip fleet-by-name resolve, which hits
              // GitHub
              ...(operation === "upgrade" && data.force ? snapshot : payload),
              operation_name: operation,
              arguments: operationArguments,
              description: description || undefined,
              gates_enabled: gatesEnabled ?? undefined,
              gate_bake_secs:
                bakeMinutes == null ? undefined : bakeMinutes * 60,
            });
          });
          setCreatedFleetOperation(created.id);
          // The op now exists: settle on the confirmation, never a
          // resubmittable form.
          setPhase("done");
          onOperationComplete?.();
        } catch (error) {
          // Back to the form with the error, so the operator can fix and retry.
          const gateBlock =
            error instanceof ApiError ? error.versionGateBlock : undefined;
          setVersionGateBlock(gateBlock ?? null);
          setFormError(
            gateBlock
              ? null
              : error instanceof Error
                ? error.message
                : "Unknown error",
          );
          setPhase("form");
        }
        return;
      }

      // Execute on each cluster sequentially
      for (let i = 0; i < clusters.length; i++) {
        const cluster = clusters[i];
        setCurrentExecutionIndex(i);

        // Server now handles filtering, so operations should only be shown when appropriate

        try {
          let operationId: string | undefined;
          // Tag edits use the tags endpoint, not the operations API.
          if (jsonData.operation === "edit_tags") {
            await api.patchClusterTags(
              cluster.id,
              jsonData.tags ? JSON.parse(jsonData.tags) : {},
            );
          } else {
            operationId = await withGithubBypass(jsonData, (data) =>
              api.createClusterOperation(cluster.id, data),
            );
          }
          setExecutionResults((prev) => [
            ...prev,
            {
              clusterId: cluster.id,
              success: true,
              operationId,
            },
          ]);
        } catch (error) {
          const versionGateBlock =
            error instanceof ApiError ? error.versionGateBlock : undefined;
          setExecutionResults((prev) => [
            ...prev,
            {
              clusterId: cluster.id,
              success: false,
              error: error instanceof Error ? error.message : "Unknown error",
              versionGateBlock,
            },
          ]);

          // Stop on error: the run settles here.
          setPhase("done");
          return;
        }

        if (i < clusters.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      // All completed successfully
      setPhase("done");
      onOperationComplete?.();
    },
    [
      clusters,
      fleetOperation,
      isFleetMode,
      fleetWaves,
      wavesDirty,
      gateBakeMinutes,
      fleetDescription,
      gatesEnabled,
      onOperationComplete,
    ],
  );

  // Navigation and escape keybindings (always active when dialog is open)
  useKeybindings(
    {
      Escape: (e: KeyboardEvent) => {
        e.preventDefault();
        if (phase === "running") {
          // Going back to the form mid-run invites a duplicate submission —
          // ignore Escape until it settles.
          return;
        }
        if (pendingRestart) {
          setPendingRestart(null);
          return;
        }
        if (isFleetMode) {
          handleDialogClose();
          return;
        }
        if (phase === "done") {
          // A completed run closes; a partial (stopped-on-error) run goes
          // back to the form for a retry.
          const completed = executionResults.length === clusters.length;
          if (completed) {
            handleDialogClose();
          } else {
            setPhase("form");
            setExecutionResults([]);
          }
        } else if (selectedOperation) {
          // If showing form, go back to operation list
          setSelectedOperation(null);
          setFormError(null);
          setVersionGateBlock(null);
          setSearchQuery("");
        } else if (searchQuery) {
          // If there's a search query, clear it first
          setSearchQuery("");
        } else {
          // If showing operation list, close dialog
          handleDialogClose();
        }
      },
    },
    {
      allowedElements: ["INPUT", "SELECT", "BUTTON", "DIV", "BODY"],
      disallowedElements: [],
      enabled: isOpen,
    },
  );

  // Operation list navigation (only when no operation is selected)
  useKeybindings(
    {
      ArrowUp: (e: KeyboardEvent) => {
        e.preventDefault();
        goUpOperationList();
      },
      ArrowDown: (e: KeyboardEvent) => {
        e.preventDefault();
        goDownOperationList();
      },
      Enter: (e: KeyboardEvent) => {
        e.preventDefault();
        if (activeIndex !== null) {
          selectPaletteIndex(activeIndex);
        }
      },
    },
    {
      allowedElements: ["INPUT", "BUTTON", "DIV", "BODY"],
      disallowedElements: [],
      enabled: isOpen && !selectedOperation,
    },
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto outline-none md:max-w-2xl"
        onEscapeKeyDown={(e) => {
          e.preventDefault(); // prevent the built-in escape handling; we'll use our custom handler
        }}
      >
        <DialogHeader className="pt-1">
          <DialogTitle>
            {selectedOperation
              ? `${getCommandDisplayName(selectedOperation)} for ${pluralize(clusters.length, "cluster")}`
              : clusters.length > 0
                ? `Operations for ${pluralize(clusters.length, "cluster")}`
                : "Search"}
          </DialogTitle>
        </DialogHeader>
        {selectedOperation ? (
          <>
            {/* Show selected clusters at the very top */}
            {phase === "form" && (clusters.length > 1 || isFleetMode) ? (
              <div className="mb-3">
                {clusters.length > 1 && !fleetOperation && fleetEligible ? (
                  <label className="mb-2 flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                    <Checkbox
                      checked={manualFleetOperation}
                      onChange={toggleFleetMode}
                      ariaLabel="fleet operation"
                    />
                    fleet operation
                  </label>
                ) : null}
                <div className="mb-2 text-sm text-gray-600">
                  {isFleetMode
                    ? "Creating one fleet operation:"
                    : `Running on ${pluralize(clusters.length, "cluster")} in parallel:`}
                </div>
                <div className="rounded-md border border-gray-200 bg-gray-50 p-2">
                  {isFleetMode ? (
                    <div className="space-y-2">
                      {!fleetOperation ? (
                        <div className="flex items-center gap-2 px-1 text-xs text-gray-600">
                          <label htmlFor="fleet-source">fleet</label>
                          <select
                            id="fleet-source"
                            value={fleetName ?? ""}
                            disabled={resolving}
                            onChange={(event) =>
                              selectFleet(event.target.value || null)
                            }
                            className="flex h-8 rounded-sm border border-input bg-white px-3 text-xs text-foreground focus-visible:border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <option value="">no fleet order</option>
                            {fleets.map((fleet) => (
                              <option key={fleet.name} value={fleet.name}>
                                {fleet.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : null}
                      <div className="px-1 py-0.5 text-xs text-gray-600">
                        {resolving
                          ? "resolving…"
                          : `${pluralize(fleetWaves.length, "wave")} · ${pluralize(fleetClusterCount, "cluster")}`}
                      </div>
                      <FleetWaveEditor
                        clusters={clusters}
                        waves={fleetWaves}
                        onChange={(waves) => {
                          setFleetWaves(waves);
                          setWavesDirty(true);
                          setFormError(null);
                          setVersionGateBlock(null);
                        }}
                      />
                      <Input
                        type="text"
                        inputSize="sm"
                        value={fleetDescription}
                        onChange={(event) =>
                          setFleetDescription(event.target.value)
                        }
                        placeholder="Description (optional), e.g. staged rollout to prod"
                        autoComplete="off"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <label
                          htmlFor="fleet-gates"
                          className="space-y-1 text-[10px] text-gray-500"
                        >
                          Gates
                          <select
                            id="fleet-gates"
                            value={
                              gatesEnabled == null
                                ? "inherit"
                                : gatesEnabled
                                  ? "enabled"
                                  : "disabled"
                            }
                            onChange={(event) =>
                              setGatesEnabled(
                                event.target.value === "inherit"
                                  ? null
                                  : event.target.value === "enabled",
                              )
                            }
                            className="flex h-8 w-full rounded-sm border border-input bg-white px-3 text-xs text-foreground focus-visible:border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <option value="inherit">Operation default</option>
                            <option value="enabled">Enabled</option>
                            <option value="disabled">Disabled</option>
                          </select>
                        </label>
                        <label
                          htmlFor="fleet-gate-bake-minutes"
                          className="space-y-1 text-[10px] text-gray-500"
                        >
                          Bake period (minutes)
                          <Input
                            id="fleet-gate-bake-minutes"
                            type="number"
                            inputSize="sm"
                            min={0}
                            step={1}
                            value={gateBakeMinutes}
                            onChange={(event) =>
                              setGateBakeMinutes(event.target.value)
                            }
                            placeholder="Default (10)"
                          />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      {clusters.map((cluster) => (
                        <div
                          key={cluster.id}
                          className="font-mono text-gray-700"
                        >
                          • {cluster.id}
                          {cluster.lock_reason != null ? (
                            <span className="ml-1 text-orange-600">
                              (locked)
                            </span>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
            {phase !== "form" ? (
              isFleetMode ? (
                <FleetCreationResult
                  creating={phase === "running"}
                  result={
                    createdFleetOperation
                      ? {
                          id: createdFleetOperation,
                          waveCount: fleetWaves.length,
                          clusterCount: fleetClusterCount,
                          description: fleetDescription.trim(),
                        }
                      : null
                  }
                />
              ) : (
                <div className="space-y-4">
                  <div className="mb-4 rounded-md bg-gray-50 p-3">
                    <p className="text-sm font-medium text-gray-900">
                      Target Clusters:
                    </p>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {clusters.map((cluster, index) => {
                        const state = getClusterBadgeState(index);
                        return (
                          <span
                            key={cluster.id}
                            className={`rounded px-2 py-1 text-xs ${getBadgeStyles(state)}`}
                          >
                            {cluster.id}
                            {getBadgeIcon(state)}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mb-4 rounded-md bg-blue-50 p-3">
                    <p className="text-sm text-blue-800">
                      {currentExecutionIndex < clusters.length ? (
                        <>
                          Running{" "}
                          <strong>
                            {getCommandDisplayName(selectedOperation!)}
                          </strong>{" "}
                          on{" "}
                          <strong>{clusters[currentExecutionIndex]?.id}</strong>{" "}
                          ({currentExecutionIndex + 1} of {clusters.length})
                        </>
                      ) : (
                        <>
                          Completed{" "}
                          <strong>
                            {getCommandDisplayName(selectedOperation!)}
                          </strong>{" "}
                          on all clusters
                        </>
                      )}
                    </p>
                    <div className="mt-2">
                      <div className="h-2 w-full rounded-full bg-gray-200">
                        <div
                          className="h-2 rounded-full bg-blue-600 transition-all duration-300"
                          style={{
                            width: `${Math.min((executionResults.length / clusters.length) * 100, 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {executionResults.length > 0 ? (
                    <div className="max-h-40 space-y-1 overflow-y-auto">
                      {executionResults.map((result) => {
                        const cluster = clusters.find(
                          (c) => c.id === result.clusterId,
                        );
                        // A tidy job's output is its pod's stdout.
                        const tidyLogsUrl =
                          result.success &&
                          result.operationId &&
                          cluster &&
                          executedOperation === "tidy"
                            ? getDatadogTidyJobLogsUrl(
                                cluster,
                                result.operationId,
                              )
                            : null;
                        return (
                          <div key={result.clusterId} className="text-sm">
                            <div className="flex items-center gap-2 py-1">
                              {result.success ? (
                                <Icon
                                  name="Check"
                                  size={14}
                                  className="text-green-600"
                                />
                              ) : (
                                <Icon
                                  name="X"
                                  size={14}
                                  className="text-red-600"
                                />
                              )}
                              <span className="font-medium text-gray-900">
                                {result.clusterId}
                              </span>
                              {!result.success && !result.versionGateBlock ? (
                                <span className="text-red-600">
                                  {result.error || "Failed"}
                                </span>
                              ) : null}
                              {tidyLogsUrl ? (
                                <a
                                  href={tidyLogsUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-800 hover:underline"
                                >
                                  <Icon name="ScrollText" size={14} />
                                  Datadog logs
                                </a>
                              ) : null}
                            </div>
                            {result.versionGateBlock ? (
                              <div className="ml-6 mt-2">
                                <VersionGateBlockError
                                  block={result.versionGateBlock}
                                />
                              </div>
                            ) : null}
                            {result.success &&
                            result.operationId &&
                            cluster?.possible_operations[
                              getExecutionOperation(selectedOperation!)
                            ]?.approval_behavior === "manual" ? (
                              <div className="ml-6 rounded bg-gray-50 p-2">
                                <KubectlCopyCommand
                                  operationId={result.operationId}
                                />
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}

                  {phase === "done" &&
                  executionResults.length === clusters.length ? (
                    <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-3">
                      <DatadogLinks clusters={clusters} variant="inline" />
                      <span className="text-xs text-gray-500">
                        Press Esc to close
                      </span>
                    </div>
                  ) : null}
                </div>
              )
            ) : null}
            {phase === "form" ? (
              <>
                {pendingRestart ? (
                  <RestartConfirmation
                    args={pendingRestart}
                    clusters={clusters}
                    onBack={() => setPendingRestart(null)}
                    onConfirm={() => {
                      executeMultiClusterOperation(pendingRestart);
                      setPendingRestart(null);
                    }}
                  />
                ) : null}
                {/* Stays mounted so Back keeps the form state. */}
                <div className={pendingRestart ? "hidden" : undefined}>
                  {renderMultiClusterForm()}
                </div>
              </>
            ) : null}
          </>
        ) : (
          <div className="space-y-2">
            {clusters.length > 0 ? (
              <div className="mb-4 rounded-md bg-gray-50 p-3">
                <p className="text-sm text-gray-700">
                  Selected clusters:{" "}
                  <strong>{clusters.map((c) => c.id).join(", ")}</strong>
                </p>
                <p className="mt-1 text-xs text-gray-600">
                  Choose an operation to execute across all selected clusters
                </p>
              </div>
            ) : null}
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                placeholder={
                  clusters.length > 0
                    ? "Search operations..."
                    : "Search operations, clusters, orgs, customers, namespaces..."
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoComplete="off"
              />
              {entitySearchLoading ? (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <SpinnerIcon className="h-4 w-4 animate-spin text-gray-400" />
                </div>
              ) : null}
            </div>
            <div
              ref={listRef}
              className="h-64 overflow-y-auto rounded-md border border-gray-200"
            >
              {paletteItems.length === 0 ? (
                <div className="flex h-32 items-center justify-center text-sm text-gray-500">
                  {emptyMessage}
                </div>
              ) : (
                paletteItems.map((item, index) => {
                  const isSelected = index === activeIndex;
                  return (
                    <Fragment key={item.key}>
                      {entityRows.length > 0 &&
                      item.sectionTitle !==
                        paletteItems[index - 1]?.sectionTitle ? (
                        <SectionHeader title={item.sectionTitle} />
                      ) : null}
                      {item.kind === "command" ? (
                        <button
                          className={cn(
                            "grid w-full grid-cols-[minmax(0,1fr)_4.75rem_1.75rem] items-center gap-x-1 px-3 py-2 text-left text-sm transition-colors",
                            isSelected
                              ? "bg-blue-50 text-blue-900"
                              : "hover:bg-gray-50",
                          )}
                          onClick={() => selectPaletteIndex(index)}
                          onMouseEnter={() => setSelectedOperationIndex(index)}
                          data-index={index}
                          data-active={isSelected}
                        >
                          <span className="font-medium">
                            {getCommandDisplayName(item.commandId)}
                          </span>
                          <span className="flex justify-center">
                            <ApprovalBadge
                              behavior={
                                possibleOperations[
                                  getExecutionOperation(item.commandId)
                                ]?.approval_behavior
                              }
                            />
                          </span>
                          <span className="flex justify-end">
                            {operationShortcuts[item.commandId] ? (
                              <kbd className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                                {operationShortcuts[item.commandId]}
                              </kbd>
                            ) : null}
                          </span>
                        </button>
                      ) : (
                        <button
                          className={cn(
                            "flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors",
                            isSelected
                              ? "bg-blue-50 text-blue-900"
                              : "hover:bg-gray-50",
                          )}
                          onClick={() => selectPaletteIndex(index)}
                          onMouseEnter={() => setSelectedOperationIndex(index)}
                          data-index={index}
                          data-active={isSelected}
                        >
                          <span className="min-w-0 truncate">{item.left}</span>
                          {item.right ? (
                            <span className="ml-3 shrink-0 font-mono text-xs text-gray-400">
                              {item.right}
                            </span>
                          ) : null}
                        </button>
                      )}
                    </Fragment>
                  );
                })
              )}
            </div>
            <div className="flex items-center justify-between pt-2 text-xs text-gray-500">
              <div className="flex items-center gap-2">
                <kbd className="rounded bg-gray-100 px-1.5 py-0.5">Up/Down</kbd>
                <span>navigate</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="rounded bg-gray-100 px-1.5 py-0.5">Enter</kbd>
                <span>select</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="rounded bg-gray-100 px-1.5 py-0.5">Esc</kbd>
                <span>close</span>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
